import {
  getMyNotifications,
  getUnreadNotificationsCount,
  markAllNotificationsAsRead,
  markNotificationAsRead,
  UserNotification,
} from '../../api/notificationsApi';
import {
  fetchNotificacoesPortal,
  marcarNotificacaoPortalLida,
  marcarTodasNotificacoesPortalLidas,
  PORTAL_COTACOES_HABILITADO,
} from '../../api/portalCotacoes/portalCotacoesApi';
import {
  getMinhasNotificacoes,
  getNotificacoesNaoLidasCount,
  marcarNotificacaoLida,
  ORCAMENTO_CILIA_HABILITADO,
} from '../../api/orcamentoCiliaApi';
import { PORTAL_COTACAO_DETALHE, PORTAL_COTACOES } from '../../views/portal_cotacoes/routes';
import { ORCAMENTOS_CILIA, ORCAMENTOS_CILIA_DETAIL } from '../../views/orcamento_cilia/routes';

/**
 * As fontes do sino do topo (portal-cotacoes/at-14, pedido do Victor de 28/10/2026: "não gosto de
 * dois sinos, unifique todos os avisos no sino geral, de todas as telas").
 *
 * Cada módulo grava os próprios avisos no próprio backend — SOAC, Portal de Cotações e
 * Requisições —, e até aqui cada um tinha um leitor: o sino do topo, o sino da tela do Portal e o
 * painel de avisos da lista de Requisições. Juntar no FRONTEND, e não copiar os avisos entre
 * backends, mantém cada módulo dono do seu dado: nenhum serviço escreve no banco de outro.
 *
 * Uma fonte só entra quando o módulo está ligado no build e a pessoa alcança a tela dele — sem
 * isso, a consulta responderia 403 e o sino acusaria falha a quem nunca teve acesso ao módulo.
 */
export interface FonteDeAvisos {
  origem: 'SOAC' | 'PORTAL' | 'REQUISICOES';
  /** Nome que aparece na faixa de erro quando só esta fonte falha. */
  rotulo: string;
  pagina: (pagina: number, limite: number) => Promise<{ itens: UserNotification[]; total: number }>;
  naoLidas: () => Promise<number>;
  marcarLida: (aviso: UserNotification) => Promise<void>;
  marcarTodas: () => Promise<void>;
}

/** O id do aviso no backend dele: os satélites ganham prefixo para não colidir no sino. */
const idOriginal = (aviso: UserNotification) => aviso.id.replace(/^(portal|requisicoes):/, '');

const fonteSoac: FonteDeAvisos = {
  origem: 'SOAC',
  rotulo: 'do SOAC',
  pagina: async (pagina, limite) => {
    const r = await getMyNotifications(pagina, limite);
    return { itens: r.items.map((n) => ({ ...n, origem: 'SOAC' as const })), total: r.total };
  },
  naoLidas: () => getUnreadNotificationsCount(),
  marcarLida: (aviso) => markNotificationAsRead(aviso.id).then(() => undefined),
  marcarTodas: () => markAllNotificationsAsRead().then(() => undefined),
};

const fontePortal: FonteDeAvisos = {
  origem: 'PORTAL',
  rotulo: 'das cotações',
  pagina: async (pagina, limite) => {
    const r = await fetchNotificacoesPortal({ page: pagina, limit: limite });
    return {
      total: r.total,
      itens: r.data.map((a) => ({
        id: `portal:${a.id}`,
        userId: '',
        type: a.tipo as UserNotification['type'],
        title: a.titulo,
        message: a.mensagem,
        read: a.lida,
        readAt: null,
        createdAt: a.createdAt,
        origem: 'PORTAL' as const,
        // Aviso que cruza várias cotações (resumo diário) não tem cotação: abre a lista.
        destino: a.cotacaoId ? PORTAL_COTACAO_DETALHE(a.cotacaoId) : PORTAL_COTACOES(),
      })),
    };
  },
  naoLidas: async () => (await fetchNotificacoesPortal({ page: 1, limit: 1 })).naoLidas,
  marcarLida: (aviso) => marcarNotificacaoPortalLida(idOriginal(aviso)).then(() => undefined),
  marcarTodas: () => marcarTodasNotificacoesPortalLidas().then(() => undefined),
};

const fonteRequisicoes: FonteDeAvisos = {
  origem: 'REQUISICOES',
  rotulo: 'das requisições',
  pagina: async (pagina, limite) => {
    const r = await getMinhasNotificacoes({ page: pagina, limit: limite });
    return {
      total: r.total,
      itens: r.data.map((a) => ({
        id: `requisicoes:${a.id}`,
        userId: '',
        type: a.tipo as UserNotification['type'],
        title: a.titulo,
        message: a.mensagem,
        read: a.lida,
        readAt: null,
        createdAt: a.createdAt,
        origem: 'REQUISICOES' as const,
        destino: a.payload?.orcamentoId ? ORCAMENTOS_CILIA_DETAIL(a.payload.orcamentoId) : ORCAMENTOS_CILIA(),
      })),
    };
  },
  naoLidas: () => getNotificacoesNaoLidasCount(),
  marcarLida: (aviso) => marcarNotificacaoLida(idOriginal(aviso)),
  /*
   * O módulo não tem rota de "marcar todas": marca uma a uma as não lidas (até 100). Acima disso
   * sobra o resto não lido, e o badge continua mostrando — nunca um falso "tudo lido".
   */
  marcarTodas: async () => {
    const r = await getMinhasNotificacoes({ page: 1, limit: 100, somenteNaoLidas: true });
    for (const a of r.data) await marcarNotificacaoLida(a.id);
  },
};

/** As fontes que esta pessoa lê, dado o que ela alcança. */
export function fontesDoUsuario(alcanca: (path: string) => boolean): FonteDeAvisos[] {
  const fontes = [fonteSoac];
  if (PORTAL_COTACOES_HABILITADO && alcanca(PORTAL_COTACOES())) fontes.push(fontePortal);
  if (ORCAMENTO_CILIA_HABILITADO && alcanca(ORCAMENTOS_CILIA())) fontes.push(fonteRequisicoes);
  return fontes;
}

/** Eventos de janela que avisam que uma fonte ganhou aviso (socket de cada módulo). */
export const EVENTOS_DE_AVISO_NOVO = ['notification:created', 'portal:notificacao', 'cilia:notificacao'];
